if (!customElements.get('product-model')) {
  customElements.define(
    'product-model',
    class ProductModel extends DeferredMedia {
      constructor() {
        super();

        const schedule = () => {
          // Prepare models in the background on product pages only.
          if (!/\/products\/[^/]+/.test(location.pathname)) return;

          const prepare = () => {
            if (this.isConnected && !this.dataset.ignore) {
              this.prepareModel().catch(() => {
                // Keep the preview visible if preparation fails.
              });
            }
          };

          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(prepare, { timeout: 2000 });
          } else {
            window.setTimeout(prepare, 500);
          }
        };

        if (document.readyState === 'complete') {
          schedule();
        } else {
          window.addEventListener('load', schedule, { once: true });
        }
      }

      prepareModel() {
        if (this.modelPreparation) return this.modelPreparation;

        this.modelPreparation = (async () => {
          const template = this.querySelector('template');
          let model = this.querySelector('model-viewer');

          if (!model) {
            const source =
              template?.content.querySelector('model-viewer');

            if (!source) {
              throw new Error('Product model markup was not found.');
            }

            model = source.cloneNode(true);
          }

          // Load even when the gallery slide is offscreen.
          model.setAttribute('loading', 'eager');
          model.setAttribute('reveal', 'auto');

          // Keep the preparing viewer out of keyboard navigation.
          model.setAttribute('inert', '');
          model.setAttribute('aria-hidden', 'true');

          const ready = new Promise((resolve, reject) => {
            if (model.loaded) {
              resolve();
              return;
            }

            const cleanup = () => {
              model.removeEventListener('load', onLoad);
              model.removeEventListener('error', onError);
            };

            const onLoad = () => {
              cleanup();
              resolve();
            };

            const onError = () => {
              cleanup();
              reject(new Error('The product model could not load.'));
            };

            model.addEventListener('load', onLoad);
            model.addEventListener('error', onError);
          });

          // Handle failures while the viewer library is loading.
          ready.catch(() => {});

          if (!model.parentElement) {
            this.appendChild(model);
          }

          this.deferredElement = model;

          await new Promise((resolve, reject) => {
            Shopify.loadFeatures([
              {
                name: 'model-viewer-ui',
                version: '1.0',
                onLoad: (errors) => {
                  if (errors) {
                    reject(errors);
                    return;
                  }

                  try {
                    this.modelViewerUI =
                      new Shopify.ModelViewerUI(model);
                    resolve();
                  } catch (error) {
                    reject(error);
                  }
                },
              },
            ]);
          });

          await ready;
          return model;
        })();

        return this.modelPreparation;
      }

      async loadContent(focus = true, play = false) {
        if (this.dataset.ignore) return;

        this.poster?.setAttribute('aria-busy', 'true');

        try {
          const model = await this.prepareModel();

          if (!this.isConnected) return;

          // Reveal only after the model has loaded.
          this.setAttribute('data-model-revealed', '');
          this.setAttribute('loaded', '');

          model.removeAttribute('inert');
          model.removeAttribute('aria-hidden');

          if (focus) model.focus();
        } catch (error) {
          // Keep the preview visible instead of a blank viewer.
          console.warn('Product 3D viewer could not load:', error);
        } finally {
          this.poster?.removeAttribute('aria-busy');
        }
      }
    }
  );
}

window.ProductModel = {
  loadShopifyXR() {
    Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: this.setupShopifyXR.bind(this),
      },
    ]);
  },

  setupShopifyXR(errors) {
    if (errors) return;

    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () =>
        this.setupShopifyXR()
      );
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });

    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener('DOMContentLoaded', () => {
  if (window.ProductModel) window.ProductModel.loadShopifyXR();
});
